import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Project } from 'shared/types';
import { projectsApi } from '@/lib/api';

export interface UseProjectsResult {
  projects: Project[];
  projectsById: Record<string, Project>;
  isLoading: boolean;
  isConnected: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch projects the current user has access to.
 * Uses the auth-filtered REST API which returns:
 *   - ALL projects for admin
 *   - Only assigned projects for other roles
 */
export function useProjects(): UseProjectsResult {
  const {
    data: rawProjects,
    isLoading,
    error,
    refetch,
  } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: projectsApi.list,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // poll every 30s for near-realtime
    refetchOnWindowFocus: true,
  });

  const projectsById = useMemo(() => {
    const map: Record<string, Project> = {};
    if (rawProjects) {
      for (const p of rawProjects) {
        map[p.id] = p;
      }
    }
    return map;
  }, [rawProjects]);

  const projects = useMemo(() => {
    return (rawProjects ?? []).slice().sort(
      (a, b) =>
        new Date(b.created_at as unknown as string).getTime() -
        new Date(a.created_at as unknown as string).getTime()
    );
  }, [rawProjects]);

  const errorObj = useMemo(
    () => (error ? (error instanceof Error ? error : new Error(String(error))) : null),
    [error]
  );

  return {
    projects,
    projectsById,
    isLoading,
    isConnected: !isLoading && !error,
    error: errorObj,
    refetch,
  };
}
